import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useModalDismiss } from "./useModalDismiss";

interface Folder {
    id: string;
    name: string;
    parentFolderId: string | null;
}

interface DocumentItem {
    id: string;
    filename: string;
    categoryId: string;
    folderId: string | null;
    isPinned: boolean;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
}

interface Category {
    id: string;
    name: string;
    icon: string | null;
    color: string | null;
}

interface Props {
    doc: DocumentItem;
    categories: Category[];
    onSave: () => void;
    onCancel: () => void;
}

function folderDepth(folderId: string, folders: Folder[], visited = new Set<string>()): number {
    if (visited.has(folderId)) return 0;
    visited.add(folderId);
    const folder = folders.find(f => f.id === folderId);
    if (!folder || !folder.parentFolderId) return 0;
    return 1 + folderDepth(folder.parentFolderId, folders, visited);
}

export default function DocumentMoveForm({ doc, categories, onSave, onCancel }: Props) {
    const [categoryId, setCategoryId] = useState<string>(doc.categoryId);
    const [folderId, setFolderId] = useState<string>(doc.folderId ?? "");
    const [folders, setFolders] = useState<Folder[]>([]);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    useEffect(() => {
        setLoadingFolders(true);
        axios.get(`/api/manuals/categories/${categoryId}/folders`)
            .then(res => {
                if (res.data.success) setFolders(res.data.items);
                setLoadingFolders(false);
            })
            .catch(() => setLoadingFolders(false));
    }, [categoryId]);

    function handleCategoryChange(newCatId: string) {
        setCategoryId(newCatId);
        setFolderId("");
    }

    const noChange = categoryId === doc.categoryId && (folderId || null) === doc.folderId;

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (noChange) return;
        setSaving(true);
        axios.put(`/api/manuals/documents/${doc.id}`, {
            categoryId,
            folderId: folderId || null,
            filename: doc.filename,
            isPinned: doc.isPinned,
            sortOrder: doc.sortOrder,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Document moved");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to move");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to move");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Move document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="m-label">Category</label>
                        <select
                            className="m-input"
                            value={categoryId}
                            onChange={e => handleCategoryChange(e.target.value)}
                        >
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Folder</label>
                        <select
                            className="m-input"
                            value={folderId}
                            onChange={e => setFolderId(e.target.value)}
                            disabled={loadingFolders}
                        >
                            <option value="">Root of category</option>
                            {folders.map(f => {
                                const depth = folderDepth(f.id, folders);
                                const indent = "\u00a0\u00a0".repeat(depth);
                                return (
                                    <option key={f.id} value={f.id}>
                                        {indent}{f.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving || noChange}>
                        {saving ? "Moving..." : "Move document"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
